import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Method,
  Prop,
  State,
  h,
} from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import { Unsubscribe, store } from '@stencil/redux';
import {
  RaboScannerChallenge,
  RaboScannerResponseInfo,
} from '../../class/class';
import {
  CreditCardType,
  Language,
  SigningMethod,
  SourceName,
} from '../../enums/const-enums';
import { uiReady } from '../../redux/actions/config-actions';
import {
  clearNotification,
  showNotification,
} from '../../redux/actions/notification-action';
import { RootState, initialStore } from '../../redux/store';
import { GetChallenge } from '../../service/get-challenge';
import { DebugLog, LogCollectorService } from '../../service/log-collector';
import {
  ApplicationData,
  EventName,
  clickEventName,
  eventButtonClick,
  getEvent,
  sendEvent,
} from '../../service/piano-analytics';
import { SignSubmit } from '../../service/sign-submit';
import { getIBAN } from '../../utils/iban';
import { isDarkMode } from '../../utils/theming';
import {
  focusSignElement,
  getActionType,
  getBaseUrl,
  handleErrorResponse,
  isMobileScreen,
  logd,
} from '../../utils/utils';
import {
  RassRaboScannerTranslations,
  rassRaboScannerTranslations,
} from './i18n/rass-rabo-scanner.i18n';

@Component({
  tag: 'rass-rabo-scanner',
  styleUrl: 'rass-rabo-scanner.scss',
  shadow: false,
})
export class RassRaboScanner {
  isConnected: boolean;
  isPreventingScroll = true;
  logger = new LogCollectorService();
  @Element() element!: HTMLStencilElement;
  @State() assetsPath: string;
  @State() language: Language;
  @State() sourceName: SourceName;
  @State() challengeData: RaboScannerChallenge;
  @State() accountNumber: string;
  @State() cardNumber: string;
  @State() creditCardType: CreditCardType;
  @State() creditCardSuffix: string;
  @State() isCardValid: boolean;
  @State() isCardSwitchAllowed: boolean;
  imageBaseUrl = getBaseUrl();
  @State() validityTimeout: ReturnType<typeof setTimeout>;
  @State() isChallengeExpired = false;
  @State() isChallengeRequestPending = false;
  @State() isSubmitRequestPending = false;
  @State() renderVideo = false;
  @Prop({ mutable: true, reflect: true }) hasErrorStatus: string = null;
  @Event({ eventName: 'code:reset' }) resetCodeEvent: EventEmitter;
  clearNotification!: typeof clearNotification;
  showNotification!: typeof showNotification;
  uiReady!: typeof uiReady;
  unsubscribe!: Unsubscribe;

  componentWillLoad(): void {
    store.setStore(initialStore);
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName },
        card: {
          accountNumber,
          cardNumber,
          creditCardType,
          creditCardSuffix,
          isCardSwitchAllowed,
          isCardValid,
        },
        challengeData,
      } = state;
      return {
        assetsPath,
        language,
        sourceName,
        accountNumber,
        cardNumber,
        creditCardType,
        creditCardSuffix,
        isCardSwitchAllowed,
        isCardValid,
        challengeData,
      };
    });
    store.mapDispatchToProps(this, {
      showNotification,
      clearNotification,
      uiReady,
    });
    this.clearNotification();
    setTimeout(() => {
      this.isPreventingScroll = false;
    }, 2000);
  }

  componentDidLoad(): void {
    this.uiReady();
  }

  componentWillRender(): void {
    if (!this.isCardValid && this.isConnected) {
      this.clearValidityTimeout();
    }
  }

  connectedCallback(): void {
    this.isConnected = true;
  }

  disconnectedCallback(): void {
    this.clearValidityTimeout();
    this.isConnected = false;
  }

  @Listen('rass:card:complete')
  getChallenge(): void {
    this.isChallengeRequestPending = true;
    new GetChallenge()
      .doGetChallenge()
      .then(() => {
        this.isChallengeRequestPending = false;
        if (this.challengeData.validity) {
          this.clearValidityTimeout();
          this.validityTimeout = setTimeout(() => {
            this.handleExpiredChallenge();
          }, this.challengeData.validity);
        }
        if (!isMobileScreen()) {
          focusSignElement('sign_code input', {
            preventScroll: this.isPreventingScroll,
          });
        }
        this.logDarkModeRSChallenge();
      })
      .catch((error: AppModel.HttpResponseErrorJson) => {
        handleErrorResponse(error, this.translations.errors.timeout);
        this.isChallengeRequestPending = false;
      });
  }

  logDarkModeRSChallengeOnce = false;
  logDarkModeRSChallenge(): void {
    if (!this.logDarkModeRSChallengeOnce && isDarkMode()) {
      this.logDarkModeRSChallengeOnce = true;
      this.logger.reportDebugLog(
        new DebugLog(
          'DARK_MODE_RS_CHALLENGE_INIT',
          SigningMethod.RABO_SCANNER,
          'INFO',
        ),
      );
    }
  }

  logDarkModeRSBrighten(): void {
    this.logger.reportDebugLog(
      new DebugLog(
        'DARK_MODE_RS_CHALLENGE_BRIGHTEN',
        SigningMethod.RABO_SCANNER,
        'INFO',
      ),
    );
  }

  sendAnalyticsEvent(name: EventName, event: ApplicationData): void {
    sendEvent(name, event).catch((err: Error) =>
      logd(() => console.error('Error while sending analytics event:', err)),
    );
  }

  @Listen('rass:code:submit')
  onCodeSubmit(event: CustomEvent<string>): void {
    this.sendAnalyticsEvent(
      clickEventName,
      getEvent(
        this.sourceName,
        SigningMethod.RABO_SCANNER,
        eventButtonClick,
        'login',
      ),
    );

    this.isSubmitRequestPending = true;
    this.clearNotification();
    const responseInfo = new RaboScannerResponseInfo(event.detail);
    new SignSubmit()
      .doSignSubmit(responseInfo)
      .catch((error: AppModel.HttpResponseErrorJson) => {
        handleErrorResponse(error, this.translations.errors.timeout);
        this.isSubmitRequestPending = false;
        if (error.faultCode && error.faultCode == '992') {
          this.handleExpiredChallenge();
        } else {
          this.resetCodeEvent.emit();
          this.getChallenge();
        }
      });
  }

  handleExpiredChallenge(): void {
    logd(() => console.log('Handle expired challenge'));
    this.isChallengeExpired = true;
    this.resetCodeEvent.emit();
  }

  clearValidityTimeout(): void {
    logd(() => console.log('Clear validity timeout'));
    if (this.validityTimeout) {
      clearTimeout(this.validityTimeout);
      this.isChallengeExpired = false;
    }
  }

  get challengeImage(): string {
    if (this.isChallengeExpired || !this.isConnected) {
      return `${this.assetsPath}images/rs-${this.imageThemeFragment}-refresh.png`;
    } else if (this.isCardValid && this.challengeData.uri) {
      return this.imageBaseUrl + this.challengeData.uri;
    }
    return `${this.assetsPath}images/rs-${this.imageThemeFragment}-standby.png`;
  }

  get imageThemeFragment(): string {
    return isDarkMode() ? 'dm' : 'lm';
  }

  get challengeInstructions(): string {
    if (!this.isCardValid) {
      return this.translations.instructions.initial;
    }
    if (this.isChallengeExpired) {
      return this.translations.instructions.expired;
    } else if (this.isCardSwitchAllowed) {
      return this.translations.instructions[getActionType()];
    }

    if (this.creditCardType === null) {
      return (
        this.translations.instructions.no_switch_allowed.leftSide +
        getIBAN(this.accountNumber) +
        this.translations.instructions.no_switch_allowed.middle +
        this.cardNumber +
        this.translations.instructions.no_switch_allowed.rightSide
      );
    } else {
      return `${this.translations.instructions.credit_card.leftSide}************${this.creditCardSuffix}${this.translations.instructions.credit_card.rightSide}`;
    }
  }

  get isRefreshable(): boolean {
    return this.isChallengeExpired && !this.isChallengeRequestPending;
  }

  get translations(): RassRaboScannerTranslations {
    return (
      rassRaboScannerTranslations[this.language] ||
      rassRaboScannerTranslations[Language.EN]
    );
  }

  @Method()
  async showVideo(): Promise<void> {
    const modalRef =
      this.element.querySelector<HTMLSfcModalElement>('#rs-video-modal');
    this.renderVideo = true;
    await modalRef.present();
  }

  closeVideo(): void {
    this.renderVideo = false;
  }

  get videoHtml() {
    return (
      <video width="480" height="320" controls autoplay>
        <source
          src="https://media.rabobank.com/m/69eb80fc36a861f4/original/Uitleg-gebruik-Rabo-Scanner.mp4"
          type="video/mp4"
        />
      </video>
    ) as void;
  }

  @State() hasWhiteBackground = false;
  setWhiteBackground(): void {
    this.hasWhiteBackground = true;
    this.logDarkModeRSBrighten();
  }

  onSetWhiteBackgroundKeyDown(event: KeyboardEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (event.key === 'Enter') {
      event.preventDefault();
      target.click();
    }
  }

  get darkModeInstructionsLabel(): string {
    return `${this.translations.instructions.dark_mode.leftSide}${this.translations.instructions.dark_mode.middle}${this.translations.instructions.dark_mode.rightSide}`;
  }

  get isDarkModeInstructionsHidden(): boolean {
    return (
      this.isChallengeExpired ||
      !this.isCardValid ||
      this.hasWhiteBackground ||
      !isDarkMode()
    );
  }

  render(): void {
    return (
      <Host class="section-rabo-scanner">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="sfc-container body-width" role="form">
          <rass-card-input
            is-disabled={
              this.isChallengeRequestPending || this.isSubmitRequestPending
            }
          />
          <div class="sfc-display-flex sfc-justify-content-center sfc-margin-block-end-150">
            <div
              class={{
                'sfc-display-inline-block': true,
                'white-background': this.hasWhiteBackground,
              }}
            >
              <div class="challenge-image">
                <img
                  id="challenge-image"
                  class={`challenge-image-rs ${this.isChallengeExpired ? ' expired' : ''}`}
                  src={this.challengeImage}
                  onClick={
                    this.isRefreshable ? (): void => this.getChallenge() : null
                  }
                  alt={this.translations.color_code}
                />
              </div>
            </div>
          </div>
          <div class="sfc-row sfc-justify-content-center">
            <div class="sfc-col-12">
              <rass-code-submit
                is-disabled={
                  !this.isCardValid ||
                  this.isChallengeExpired ||
                  this.isSubmitRequestPending
                }
              />
            </div>
          </div>
          <div class="sfc-row sfc-justify-content-center sfc-margin-block-end-100">
            <div class="sfc-col-12">
              <div
                id="challenge-instructions"
                class={`${this.isChallengeExpired ? 'sfc-margin-block-end-0' : ''}`}
                innerHTML={
                  this.isChallengeExpired
                    ? this.challengeInstructions
                    : `<p class='sfc-paragraph sfc-font-text-align-center'>${this.challengeInstructions}</p>`
                }
              />
              <sfc-modal-sheet
                id="rs-video-modal"
                onSfcModalDidDismiss={(): void => this.closeVideo()}
              >
                <div slot="body">{this.renderVideo ? this.videoHtml : ''}</div>
              </sfc-modal-sheet>
            </div>
          </div>
          <sfc-message
            class={{
              'sfc-margin-block-end-300 block': true,
              'sfc-display-none': this.isDarkModeInstructionsHidden,
            }}
          >
            {this.translations.instructions.dark_mode.leftSide}
            <a
              class="sfc-link sfc-text-link--inline cursor-pointer"
              aria-label={this.darkModeInstructionsLabel}
              role="button"
              tabIndex={0}
              onClick={(): void => this.setWhiteBackground()}
              onKeyDown={(event: KeyboardEvent): void =>
                this.onSetWhiteBackgroundKeyDown(event)
              }
            >
              {this.translations.instructions.dark_mode.middle}
            </a>
            {this.translations.instructions.dark_mode.rightSide}
          </sfc-message>
        </div>
      </Host>
    ) as void;
  }
}
